import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, CreditCard, Lock, ShieldCheck,
  Truck, Store, Zap, Loader2, ChevronRight, Tag, X as XIcon, Globe,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/lib/cart";
import { api as apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

/* ---------- Shipping & Payment option configs ---------- */
/* Preise kommen aus dem Admin (Einstellungen → Versand) via /checkout/shipping-config.
   Diese Werte sind nur der Fallback, bis die Konfiguration geladen ist. */
const SHIPPING_DEFAULTS = {
  fee_at: 3.99,
  threshold_at: 50.0,
  fee_above_at: 0.0,        // 0 = gratis, null = keine Reduktion
  fee_intl: 9.99,
  threshold_intl: 50.0,
  fee_above_intl: 4.99,
  countries: [],
};

const AT_ALIASES = ["österreich", "oesterreich", "osterreich", "austria", "at"];
const isAustria = (c) => AT_ALIASES.includes(String(c || "").trim().toLowerCase());
const norm = (v) => String(v || "").trim().toLowerCase();

const buildShippingOptions = (atCountry, base, threshold, feeAbove) => {
  const suffix =
    feeAbove === null || feeAbove === undefined
      ? ""
      : Number(feeAbove) === 0
        ? ` · gratis ab ${fmt(threshold)}`
        : ` · ab ${fmt(threshold)} nur ${fmt(feeAbove)}`;
  return [
    atCountry
      ? {
          id: "standard", name: "Standard Versand", price: base, icon: Truck,
          desc: `2–3 Werktage · Österreichische Post${suffix}`,
        }
      : {
          id: "standard", name: "Auslandsversand", price: base, icon: Globe,
          desc: `3–6 Werktage · Versand außerhalb Österreichs${suffix}`,
        },
    { id: "pickup", name: "Abholung im Shop", desc: "Hernalser Hauptstr. 135, 1170 Wien", price: 0, icon: Store },
  ];
};

const PAYMENT_METHODS = [
  { id: "card",      name: "Kreditkarte",         desc: "Visa · Mastercard · AmEx", brand: "stripe" },
  { id: "apple_pay", name: "Apple Pay",           desc: "Schnell & sicher mit Touch / Face ID", brand: "apple" },
  { id: "google_pay",name: "Google Pay",          desc: "Express-Checkout in einem Klick", brand: "google" },
];

const SANTANDER_PLANS = [
  { months: 6,  apr: 0.0,  label: "6 Monate · 0%" },
  { months: 12, apr: 5.9,  label: "12 Monate · 5,9%" },
  { months: 24, apr: 7.9,  label: "24 Monate · 7,9%" },
];

/* ---------- Helpers ---------- */
const fmt = (n) => `€${Number(n).toFixed(2)}`;

const STEPS = [
  { id: 1, name: "Adresse" },
  { id: 2, name: "Versand" },
  { id: 3, name: "Zahlung" },
  { id: 4, name: "Überprüfen" },
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, total, clear } = useCart();
  const [step, setStep] = useState(1);
  const [processing, setProcessing] = useState(false);

  const [contact, setContact] = useState({
    email: "", first_name: "", last_name: "", phone: "",
    street: "", zip: "", city: "", country: "Österreich",
  });
  const [shipping, setShipping] = useState("standard");
  const [payment,  setPayment]  = useState("card");
  const [klarnaConfirmed, setKlarnaConfirmed] = useState(false);
  const [santanderPlan, setSantanderPlan] = useState(12);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [shipCfg, setShipCfg] = useState(SHIPPING_DEFAULTS);

  useEffect(() => {
    apiClient
      .get("/checkout/shipping-config")
      .then(({ data }) => data && setShipCfg((c) => ({ ...c, ...data })))
      .catch(() => { /* Fallback-Preise bleiben aktiv */ });
  }, []);

  // Discount / promo code state
  const [discount, setDiscount] = useState(null);    // {code, percent, amount} when applied
  const [discountInput, setDiscountInput] = useState("");
  const [discountStatus, setDiscountStatus] = useState({ loading: false, error: "" });

  const atCountry = isAustria(contact.country);
  // Shoppreise sind brutto (inkl. 20% USt AT). KEINE +20% mehr aufschlagen!
  const subtotal = total;                       // brutto sum
  const countryOptions = useMemo(() => {
    const rows = (shipCfg.countries || []).filter((c) => c && c.active !== false).map((c) => c.name);
    return ["Österreich", ...rows, "Anderes Land"];
  }, [shipCfg]);
  const countryRule = useMemo(
    () => (shipCfg.countries || []).find((c) => norm(c?.name) === norm(contact.country) && c?.active !== false) || null,
    [shipCfg, contact.country],
  );
  // Preis je Land: eigene Regel > Ausland-Standard (alles aus den Admin-Einstellungen)
  const baseFee = Number(atCountry ? shipCfg.fee_at : countryRule?.fee ?? shipCfg.fee_intl);
  const threshold = Number(atCountry ? shipCfg.threshold_at : countryRule?.threshold ?? shipCfg.threshold_intl);
  const feeAbove = atCountry
    ? shipCfg.fee_above_at
    : countryRule?.fee_above ?? shipCfg.fee_above_intl;
  const hasReduction = feeAbove !== null && feeAbove !== undefined;
  const reachedThreshold = hasReduction && subtotal >= threshold;
  const shipFee = reachedThreshold ? Number(feeAbove) : baseFee;
  const shippingOptions = useMemo(
    () => buildShippingOptions(atCountry, baseFee, threshold, hasReduction ? Number(feeAbove) : null),
    [atCountry, baseFee, threshold, hasReduction, feeAbove],
  );
  const shippingObj = shippingOptions.find((s) => s.id === shipping) || shippingOptions[0];
  const shippingFee = shipping === "pickup" ? 0 : shipFee;   // brutto
  const discountAmount = discount ? Math.round(subtotal * discount.percent) / 100 : 0;
  const grandTotal = Math.max(0, subtotal + shippingFee - discountAmount);  // = Endbetrag
  const tax = grandTotal - grandTotal / 1.20;   // enthaltene USt (extracted)

  const applyDiscount = async () => {
    const raw = discountInput.trim().toUpperCase();
    if (!raw) {
      setDiscountStatus({ loading: false, error: "Bitte gib einen Code ein" });
      return;
    }
    setDiscountStatus({ loading: true, error: "" });
    try {
      const { data } = await apiClient.post("/discount/validate", { code: raw, subtotal });
      if (data?.valid) {
        setDiscount({ code: data.code, percent: data.percent, amount: data.discount });
        setDiscountStatus({ loading: false, error: "" });
        toast.success(`Code "${data.code}" angewendet · -${data.percent}%`);
      } else {
        setDiscount(null);
        setDiscountStatus({ loading: false, error: data?.message || "Code ungültig" });
      }
    } catch (e) {
      setDiscount(null);
      setDiscountStatus({ loading: false, error: e?.response?.data?.detail || "Code konnte nicht geprüft werden" });
    }
  };

  const removeDiscount = () => {
    setDiscount(null);
    setDiscountInput("");
    setDiscountStatus({ loading: false, error: "" });
  };

  const monthlyKlarna  = useMemo(() => grandTotal / 3, [grandTotal]);
  const monthlySantander = useMemo(() => {
    const plan = SANTANDER_PLANS.find((p) => p.months === santanderPlan) || SANTANDER_PLANS[1];
    const r = plan.apr / 100 / 12;
    if (r === 0) return grandTotal / plan.months;
    return (grandTotal * r) / (1 - Math.pow(1 + r, -plan.months));
  }, [grandTotal, santanderPlan]);

  /* ---------- Validation per step ---------- */
  const canContinue = () => {
    if (step === 1) {
      return contact.email && contact.first_name && contact.last_name && contact.phone &&
             contact.street && contact.zip && contact.city;
    }
    if (step === 2) return Boolean(shipping);
    if (step === 3) {
      if (payment === "klarna") return klarnaConfirmed;
      if (payment === "santander") return santanderPlan > 0;
      return true; // card / apple pay / google pay → data is entered securely at Stripe
    }
    if (step === 4) return acceptedTerms;
    return true;
  };

  const next = () => {
    if (!canContinue()) {
      toast.error("Bitte alle Pflichtfelder korrekt ausfüllen");
      return;
    }
    setStep((s) => Math.min(STEPS.length, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  /* ---------- Submit ---------- */
  // For real Stripe-supported methods (card, apple_pay, google_pay, klarna) the
  // backend creates a Stripe Checkout Session and we redirect to Stripe. For
  // Santander (which has no Stripe equivalent) we keep the visual mock flow.
  const submit = async () => {
    if (!acceptedTerms) {
      toast.error("Bitte AGB und Datenschutz akzeptieren");
      return;
    }
    setProcessing(true);

    const isStripeMethod = ["card", "apple_pay", "google_pay", "klarna"].includes(payment);
    if (isStripeMethod) {
      try {
        // Persist contact/summary for the success page in case redirect comes back
        sessionStorage.setItem("hdh_pending_order", JSON.stringify({
          contact, shipping: shippingObj, payment, items, subtotal, shippingFee, tax, grandTotal,
          discount,
        }));
        const { data } = await apiClient.post("/checkout/create-session", {
          origin_url: window.location.origin,
          items: items.map((i) => ({ sku: i.sku, qty: i.qty })),
          shipping_id: shipping,
          payment_method: payment === "klarna" ? "klarna" : "card",
          discount_code: discount?.code || "",
          contact: {
            email: contact.email,
            first_name: contact.first_name,
            last_name: contact.last_name,
            phone: contact.phone,
            street: contact.street,
            zip: contact.zip,
            city: contact.city,
            country: contact.country,
          },
        });
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error("Keine Checkout-URL erhalten");
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Stripe-Fehler";
        toast.error(`Zahlung konnte nicht gestartet werden: ${msg}`);
        setProcessing(false);
        return;
      }
    }

    // Santander = visual mockup flow (no real API yet)
    await new Promise((r) => setTimeout(r, 2200));
    const orderId = `HDH-${Date.now().toString().slice(-7)}`;
    const summary = {
      orderId,
      contact, shipping: shippingObj, payment,
      santanderPlan: payment === "santander" ? santanderPlan : null,
      items, subtotal, shippingFee, tax, grandTotal,
      created_at: new Date().toISOString(),
      mock: true,
    };
    sessionStorage.setItem("hdh_last_order", JSON.stringify(summary));
    clear();
    navigate("/checkout/erfolg", { replace: true });
  };

  /* ---------- Empty cart fallback ---------- */
  if (items.length === 0 && !processing) {
    return (
      <div className="pt-32 pb-24 bg-[#0A0A0B] min-h-screen" data-testid="checkout-empty">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Warenkorb leer</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mt-3">Noch nichts ausgewählt.</h1>
          <p className="text-zinc-400 mt-4">Stöbern Sie in unserem Zubehör-Sortiment, um Ihre Bestellung zu starten.</p>
          <Link to="/zubehoer">
            <Button className="mt-8 bg-[#DC1928] hover:bg-[#B01829] rounded-full h-12 px-7" data-testid="empty-shop-btn">
              Zubehör entdecken <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-20 bg-[#0A0A0B] min-h-screen text-white" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/zubehoer" className="text-sm text-zinc-500 hover:text-white inline-flex items-center gap-1" data-testid="checkout-back-shop">
            <ArrowLeft className="w-4 h-4" /> Weiter einkaufen
          </Link>
          <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 inline-flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5 text-emerald-400" />
            <span>Sichere SSL-Verbindung</span>
          </div>
        </div>

        <div className="mb-2 text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Kasse</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold leading-[1.02] tracking-tight">Bestellung abschließen.</h1>
        <p className="text-zinc-500 mt-2 text-sm">Sicher bezahlen mit Kreditkarte · Apple Pay · Google Pay</p>

        {/* Stepper */}
        <Stepper step={step} />

        <div className="mt-10 grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left column — form. translate="no" keeps browser translators (Chrome/Safari
              Translate) out of the most dynamic DOM area, which is what crashed the app. */}
          <div className="lg:col-span-7 space-y-6 notranslate" translate="no">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="step-1" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
                  <AddressStep
                    contact={contact}
                    setContact={setContact}
                    countryOptions={countryOptions}
                    baseFee={baseFee}
                    threshold={threshold}
                    feeAbove={hasReduction ? Number(feeAbove) : null}
                  />
                </motion.div>
              )}
              {step === 2 && (
                <motion.div key="step-2" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
                  <ShippingStep
                    value={shipping}
                    onChange={setShipping}
                    subtotal={subtotal}
                    options={shippingOptions}
                    atCountry={atCountry}
                    country={contact.country}
                    baseFee={baseFee}
                    threshold={threshold}
                    feeAbove={hasReduction ? Number(feeAbove) : null}
                    currentFee={shipFee}
                  />
                </motion.div>
              )}
              {step === 3 && (
                <motion.div key="step-3" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
                  <PaymentStep
                    payment={payment} setPayment={setPayment}
                    klarnaConfirmed={klarnaConfirmed} setKlarnaConfirmed={setKlarnaConfirmed}
                    santanderPlan={santanderPlan} setSantanderPlan={setSantanderPlan}
                    monthlyKlarna={monthlyKlarna}
                    monthlySantander={monthlySantander}
                    grandTotal={grandTotal}
                  />
                </motion.div>
              )}
              {step === 4 && (
                <motion.div key="step-4" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
                  <ReviewStep
                    contact={contact}
                    shippingObj={shippingObj}
                    shippingFee={shippingFee}
                    payment={payment}
                    santanderPlan={santanderPlan}
                    monthlyKlarna={monthlyKlarna}
                    monthlySantander={monthlySantander}
                    acceptedTerms={acceptedTerms}
                    setAcceptedTerms={setAcceptedTerms}
                    onEdit={(s) => setStep(s)}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-between sm:items-center pt-2">
              <Button
                variant="outline"
                onClick={back}
                disabled={step === 1 || processing}
                className="bg-transparent border-white/15 text-zinc-300 hover:bg-white/5 hover:text-white rounded-full h-12 px-6"
                data-testid="checkout-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
              </Button>
              {step < STEPS.length ? (
                <Button
                  onClick={next}
                  className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-12 px-7 btn-lift glow-red-sm"
                  data-testid="checkout-next-btn"
                >
                  Weiter <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={!acceptedTerms || processing}
                  className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-12 px-7 btn-lift glow-red disabled:opacity-50"
                  data-testid="checkout-pay-btn"
                >
                  {processing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird verarbeitet…</>
                  ) : (
                    <><Lock className="w-4 h-4 mr-2" /> {fmt(grandTotal)} sicher bezahlen</>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Right column — summary */}
          <aside className="lg:col-span-5 notranslate" translate="no">
            <OrderSummary
              items={items}
              subtotal={subtotal}
              shippingObj={shippingObj}
              shippingFee={shippingFee}
              tax={tax}
              grandTotal={grandTotal}
              payment={payment}
              monthlyKlarna={monthlyKlarna}
              monthlySantander={monthlySantander}
              santanderPlan={santanderPlan}
              discount={discount}
              discountAmount={discountAmount}
              discountInput={discountInput}
              setDiscountInput={setDiscountInput}
              discountStatus={discountStatus}
              applyDiscount={applyDiscount}
              removeDiscount={removeDiscount}
            />
          </aside>
        </div>
      </div>
    </div>
  );
}

/* ============================================================ */
/*  Sub-components                                                */
/* ============================================================ */

function Stepper({ step }) {
  return (
    <div className="mt-10 flex items-center gap-2 overflow-x-auto" data-testid="checkout-stepper">
      {STEPS.map((s, i) => {
        const done = step > s.id;
        const active = step === s.id;
        return (
          <div key={s.id} className="flex items-center shrink-0">
            <div
              className={`flex items-center gap-2.5 ${
                active ? "text-white" : done ? "text-emerald-400" : "text-zinc-600"
              }`}
              data-testid={`step-indicator-${s.id}`}
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold border ${
                  done
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                    : active
                    ? "bg-[#DC1928] border-[#DC1928] text-white"
                    : "bg-transparent border-white/15 text-zinc-500"
                }`}
              >
                {done ? <Check className="w-3.5 h-3.5" /> : s.id}
              </div>
              <span className={`text-xs uppercase tracking-[0.18em] font-semibold ${active ? "text-white" : ""}`}>{s.name}</span>
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRight className={`w-4 h-4 mx-2 ${done ? "text-emerald-500/60" : "text-zinc-700"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Card({ title, subtitle, children, ...rest }) {
  return (
    <div className="rounded-3xl bg-[#141417] border border-white/5 p-6 md:p-8" {...rest}>
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight">{title}</h2>
        {subtitle && <span className="text-xs text-zinc-500">{subtitle}</span>}
      </div>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function Field({ label, children, full = false }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const inputCls = "bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#DC1928] focus:ring-0 h-11";

function AddressStep({ contact, setContact, countryOptions, baseFee, threshold, feeAbove }) {
  const set = (k, v) => setContact((c) => ({ ...c, [k]: v }));
  return (
    <Card title="Lieferadresse" subtitle="Schritt 1 von 4" data-testid="step-address">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="E-Mail" full>
          <Input value={contact.email} onChange={(e) => set("email", e.target.value)} placeholder="ihre@email.at" type="email" className={inputCls} data-testid="checkout-email" />
        </Field>
        <Field label="Vorname">
          <Input value={contact.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="Max" className={inputCls} data-testid="checkout-first-name" />
        </Field>
        <Field label="Nachname">
          <Input value={contact.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Mustermann" className={inputCls} data-testid="checkout-last-name" />
        </Field>
        <Field label="Telefon" full>
          <Input value={contact.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+43 660 1234567" className={inputCls} data-testid="checkout-phone" />
        </Field>
        <Field label="Straße & Hausnummer" full>
          <Input value={contact.street} onChange={(e) => set("street", e.target.value)} placeholder="Hernalser Hauptstraße 135" className={inputCls} data-testid="checkout-street" />
        </Field>
        <Field label="PLZ">
          <Input value={contact.zip} onChange={(e) => set("zip", e.target.value)} placeholder="1170" className={inputCls} data-testid="checkout-zip" />
        </Field>
        <Field label="Stadt">
          <Input value={contact.city} onChange={(e) => set("city", e.target.value)} placeholder="Wien" className={inputCls} data-testid="checkout-city" />
        </Field>
        <Field label="Land" full>
          <Select value={contact.country} onValueChange={(v) => set("country", v)}>
            <SelectTrigger className={inputCls} data-testid="checkout-country">
              <SelectValue placeholder="Land wählen" />
            </SelectTrigger>
            <SelectContent className="bg-[#141417] border-white/10 text-white max-h-72">
              {countryOptions.map((c) => (
                <SelectItem key={c} value={c} className="focus:bg-white/10 focus:text-white" data-testid={`country-option-${c}`}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-zinc-500 mt-2" data-testid="country-shipping-hint">
            Versand nach {contact.country}: <span className="numeric">{fmt(baseFee)}</span>
            {feeAbove === null || feeAbove === undefined
              ? ""
              : Number(feeAbove) === 0
                ? ` · ab ${fmt(threshold)} gratis`
                : ` · ab ${fmt(threshold)} nur ${fmt(feeAbove)}`}
          </p>
        </Field>
      </div>
    </Card>
  );
}

function ShippingStep({ value, onChange, subtotal, options, atCountry, country, baseFee, threshold, feeAbove, currentFee }) {
  const hasReduction = feeAbove !== null && feeAbove !== undefined;
  const reached = hasReduction && subtotal >= threshold;
  const missing = Math.max(0, threshold - subtotal);
  return (
    <Card title="Versandart" subtitle="Schritt 2 von 4" data-testid="step-shipping">
      {!atCountry && (
        <div className="mb-3 p-3 rounded-xl bg-white/5 border border-white/10 text-xs text-zinc-300" data-testid="intl-shipping-hint">
          Lieferung nach <span className="font-semibold text-white">{country || "Ausland"}</span> —
          Versandkosten <span className="font-display font-bold numeric">{fmt(currentFee)}</span>.
          {hasReduction && !reached && (Number(feeAbove) === 0
            ? ` Ab ${fmt(threshold)} Bestellwert gratis.`
            : ` Ab ${fmt(threshold)} Bestellwert nur ${fmt(feeAbove)}.`)}
          {!hasReduction && " Gratisversand gilt hier nicht."}
        </div>
      )}
      {hasReduction && !reached && missing > 0 && (
        <div className="mb-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300" data-testid="free-shipping-hint">
          Nur noch <span className="font-display font-bold numeric">{fmt(missing)}</span> bis zum{" "}
          <span className="font-semibold">{Number(feeAbove) === 0 ? "Gratisversand" : `reduzierten Versand (${fmt(feeAbove)})`}</span>.
        </div>
      )}
      <RadioGroup value={value} onValueChange={onChange} className="space-y-3">
        {options.map((opt) => {
          const Icon = opt.icon;
          const active = value === opt.id;
          const isReduced = reached && opt.id !== "pickup";
          const displayPrice = isReduced ? Number(feeAbove) : opt.price;
          return (
            <label
              key={opt.id}
              htmlFor={`ship-${opt.id}`}
              className={`flex items-center gap-4 p-4 rounded-2xl border cursor-pointer transition-all ${
                active
                  ? "border-[#DC1928] bg-[#DC1928]/5 ring-1 ring-[#DC1928]/30"
                  : "border-white/10 bg-[#0F0F11] hover:border-white/20"
              }`}
              data-testid={`shipping-${opt.id}`}
            >
              <RadioGroupItem value={opt.id} id={`ship-${opt.id}`} className="border-white/30 text-[#DC1928]" />
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-[#DC1928] text-white" : "bg-white/5 text-zinc-400"}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-white">{opt.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{opt.desc}</div>
              </div>
              <div className="font-display font-semibold numeric text-right">
                {displayPrice === 0 ? (
                  <span className="text-emerald-400">Gratis</span>
                ) : (
                  <>
                    {isReduced && <div className="text-[10px] text-zinc-500 line-through">{fmt(opt.price)}</div>}
                    <span>{fmt(displayPrice)}</span>
                  </>
                )}
              </div>
            </label>
          );
        })}
      </RadioGroup>
    </Card>
  );
}

function PaymentStep({ payment, setPayment, klarnaConfirmed, setKlarnaConfirmed, santanderPlan, setSantanderPlan, monthlyKlarna, monthlySantander, grandTotal }) {
  return (
    <Card title="Zahlungsmethode" subtitle="Schritt 3 von 4" data-testid="step-payment">
      <RadioGroup value={payment} onValueChange={setPayment} className="space-y-3">
        {PAYMENT_METHODS.map((m) => {
          const active = payment === m.id;
          return (
            <div key={m.id}>
              <label
                htmlFor={`pay-${m.id}`}
                className={`flex items-center gap-4 p-4 rounded-2xl border cursor-pointer transition-all ${
                  active
                    ? "border-[#DC1928] bg-[#DC1928]/5 ring-1 ring-[#DC1928]/30"
                    : "border-white/10 bg-[#0F0F11] hover:border-white/20"
                }`}
                data-testid={`payment-${m.id}`}
              >
                <RadioGroupItem value={m.id} id={`pay-${m.id}`} className="border-white/30 text-[#DC1928]" />
                <BrandLogo brand={m.brand} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-white">{m.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{m.desc}</div>
                </div>
                {m.id === "klarna" && (
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">3 Raten à</div>
                    <div className="font-display font-semibold numeric text-white">{fmt(monthlyKlarna)}</div>
                  </div>
                )}
                {m.id === "santander" && (
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">ab</div>
                    <div className="font-display font-semibold numeric text-white">{fmt(grandTotal / 24)}/Mo</div>
                  </div>
                )}
              </label>

              {/* Per-method expanded panel */}
              <AnimatePresence>
                {active && m.id === "card" && (
                  <ExpandedPanel testid="card-form">
                    <CardInfoPanel />
                  </ExpandedPanel>
                )}
                {active && m.id === "apple_pay" && (
                  <ExpandedPanel testid="apple-pay-form">
                    <ApplePayPanel />
                  </ExpandedPanel>
                )}
                {active && m.id === "google_pay" && (
                  <ExpandedPanel testid="google-pay-form">
                    <GooglePayPanel />
                  </ExpandedPanel>
                )}
                {active && m.id === "klarna" && (
                  <ExpandedPanel testid="klarna-form">
                    <KlarnaPanel
                      total={grandTotal}
                      monthly={monthlyKlarna}
                      confirmed={klarnaConfirmed}
                      onConfirm={setKlarnaConfirmed}
                    />
                  </ExpandedPanel>
                )}
                {active && m.id === "santander" && (
                  <ExpandedPanel testid="santander-form">
                    <SantanderPanel
                      total={grandTotal}
                      plan={santanderPlan}
                      onPlan={setSantanderPlan}
                      monthly={monthlySantander}
                    />
                  </ExpandedPanel>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </RadioGroup>

      <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-zinc-500">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
        <span>Powered by Stripe · Ihre Zahlungsdaten werden verschlüsselt übertragen.</span>
      </div>
    </Card>
  );
}

function ExpandedPanel({ children, testid }) {
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="overflow-hidden"
      data-testid={testid}
    >
      <div className="mt-3 p-5 rounded-2xl bg-[#0A0A0B] border border-white/10 ml-9">{children}</div>
    </motion.div>
  );
}

function CardInfoPanel() {
  return (
    <div className="space-y-3" data-testid="card-form-inner">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
          <CreditCard className="w-5 h-5 text-zinc-300" />
        </div>
        <div>
          <div className="text-sm text-white font-medium">Kartenzahlung über Stripe</div>
          <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
            Ihre Kartendaten geben Sie im nächsten Schritt direkt bei Stripe ein —
            verschlüsselt, PCI-zertifiziert und ohne Speicherung bei uns.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {["Visa", "Mastercard", "American Express", "3D Secure"].map((b) => (
          <span key={b} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] text-zinc-400">{b}</span>
        ))}
      </div>
      <p className="text-[11px] text-zinc-500 flex items-center gap-1.5 pt-1">
        <Lock className="w-3 h-3" /> Bezahlung erfolgt auf der gesicherten Stripe-Seite
      </p>
    </div>
  );
}

function ApplePayPanel() {
  return (
    <div className="text-center py-3" data-testid="apple-pay-inner">
      <div className="inline-flex items-center gap-3 mb-3">
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white"><path d="M17.05 12.91c-.03-2.52 2.05-3.74 2.14-3.8-1.17-1.71-2.99-1.95-3.64-1.98-1.55-.16-3.03.91-3.81.91-.79 0-1.99-.89-3.28-.86-1.69.03-3.25.98-4.11 2.49-1.76 3.05-.45 7.55 1.27 10.02.83 1.21 1.83 2.57 3.13 2.52 1.26-.05 1.74-.81 3.27-.81 1.52 0 1.96.81 3.3.78 1.36-.02 2.23-1.23 3.06-2.45.97-1.4 1.37-2.78 1.4-2.85-.03-.01-2.69-1.03-2.73-4.07zM14.62 5.18c.69-.84 1.16-2.01 1.03-3.18-1 .04-2.21.67-2.93 1.51-.64.74-1.21 1.93-1.06 3.08 1.12.09 2.26-.57 2.96-1.41z"/></svg>
        <span className="font-display text-lg font-semibold">Pay</span>
      </div>
      <p className="text-sm text-zinc-400">Beim Klick auf &bdquo;Sicher bezahlen&ldquo; wird das Apple Pay Sheet geöffnet.</p>
      <p className="text-[11px] text-zinc-600 mt-2">Touch ID · Face ID · Apple Watch</p>
    </div>
  );
}

function GooglePayPanel() {
  return (
    <div className="text-center py-3" data-testid="google-pay-inner">
      <div className="inline-flex items-center gap-2 mb-3">
        <svg viewBox="0 0 24 24" className="w-7 h-7"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09a6.61 6.61 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
        <span className="font-display text-lg font-semibold">Pay</span>
      </div>
      <p className="text-sm text-zinc-400">Beim Klick auf &bdquo;Sicher bezahlen&ldquo; wird das Google Pay Sheet geöffnet.</p>
      <p className="text-[11px] text-zinc-600 mt-2">Karten aus Ihrem Google Konto</p>
    </div>
  );
}

function KlarnaPanel({ total, monthly, confirmed, onConfirm }) {
  return (
    <div data-testid="klarna-inner">
      <div className="rounded-2xl bg-[#FFB3C7] text-black p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase font-bold tracking-wider">Klarna · 3 zinsfreie Raten</div>
            <div className="font-display text-2xl font-bold mt-1 numeric">{fmt(monthly)} <span className="text-sm font-medium opacity-70">/Rate</span></div>
          </div>
          <div className="text-right text-xs">
            <div className="opacity-70">Gesamt</div>
            <div className="font-display font-bold numeric">{fmt(total)}</div>
          </div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-3 rounded-xl border border-white/10 bg-[#141417] text-center">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Rate {i}</div>
            <div className="text-xs text-zinc-400 mt-1">{i === 1 ? "Heute" : `In ${30 * (i - 1)} Tagen`}</div>
            <div className="font-display font-semibold text-white numeric mt-1">{fmt(monthly)}</div>
          </div>
        ))}
      </div>
      <label className="mt-4 flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => onConfirm(e.target.checked)}
          className="mt-0.5 accent-[#DC1928]"
          data-testid="klarna-confirm"
        />
        <span className="text-xs text-zinc-400">
          Ich bestätige, dass ich die <span className="text-white underline">Klarna Nutzungsbedingungen</span> gelesen habe und einer Bonitätsprüfung zustimme.
        </span>
      </label>
    </div>
  );
}

function SantanderPanel({ total, plan, onPlan, monthly }) {
  return (
    <div data-testid="santander-inner">
      <div className="rounded-2xl bg-gradient-to-br from-[#EC0000] to-[#990000] text-white p-4">
        <div className="flex items-center justify-between">
          <div className="font-display font-bold text-lg">Santander Consumer Bank</div>
          <span className="text-[10px] uppercase tracking-[0.18em] bg-white/20 px-2 py-0.5 rounded-full">Ratenkauf</span>
        </div>
        <div className="mt-3 text-2xl font-display font-bold numeric">{fmt(monthly)} <span className="text-sm font-medium opacity-70">/Monat</span></div>
        <div className="text-xs opacity-80 mt-0.5">Über {plan} Monate · Gesamtbetrag {fmt(monthly * plan)}</div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {SANTANDER_PLANS.map((p) => {
          const active = plan === p.months;
          return (
            <button
              key={p.months}
              type="button"
              onClick={() => onPlan(p.months)}
              className={`p-3 rounded-xl border text-left transition-all ${
                active ? "border-[#DC1928] bg-[#DC1928]/10" : "border-white/10 bg-[#141417] hover:border-white/20"
              }`}
              data-testid={`santander-plan-${p.months}`}
            >
              <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-semibold">{p.label}</div>
              <div className={`font-display font-semibold mt-1 numeric ${active ? "text-white" : "text-zinc-300"}`}>{fmt((total * (p.apr/100/12 || 1/p.months)) / (p.apr === 0 ? 1 : (1 - Math.pow(1 + p.apr/100/12, -p.months))))}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">pro Monat</div>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">Repräsentatives Beispiel · Bonität vorausgesetzt · Vermittlung durch Santander Consumer Bank AG</p>
    </div>
  );
}

function BrandLogo({ brand }) {
  const cls = "w-12 h-8 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-[10px] font-bold";
  if (brand === "stripe") {
    return (
      <div className={`${cls} text-white`}>
        <svg viewBox="0 0 60 25" className="w-10 h-5 fill-current"><path d="M59.5 14.4c0-4.3-2.1-7.7-6.1-7.7s-6.4 3.4-6.4 7.6c0 5 2.8 7.6 6.9 7.6 2 0 3.5-.5 4.6-1.1V17.4c-1.1.6-2.4.9-4 .9-1.6 0-3-.6-3.2-2.5h8.1c.1-.4.1-1 .1-1.4zM51.3 12.7c0-1.8.7-2.9 2-2.9 1.2 0 2 1.1 2 2.9h-4zM40 6.7c-1.6 0-2.7.8-3.2 1.3l-.2-1H32.3v19.5l4.3-.9V21c.6.5 1.6 1.1 3.3 1.1 3.3 0 6.4-2.7 6.4-7.7 0-4.6-3.1-7.7-6.3-7.7zm-1 11.7c-1.1 0-1.8-.4-2.3-1l0-7c.5-.5 1.2-.9 2.3-.9 1.7 0 2.9 1.9 2.9 4.5 0 2.7-1.2 4.4-2.9 4.4zM30.4 5.5l4.4-.9V1l-4.4.9v3.6zm0 1.5h4.4v14.9h-4.4V7zM25.6 8.3l-.3-1.3h-3.7V22h4.3V11.9c1-1.3 2.8-1 3.3-.9V7c-.6-.2-2.6-.5-3.6 1.3zM17.3 3.3 13.1 4.2l0 13.7c0 2.5 1.9 4.4 4.4 4.4 1.4 0 2.4-.3 3-.5v-3.5c-.5.2-3 .9-3-1.6V10.7h3v-3.7h-3l0-3.7zM4.6 11.2c0-.7.5-.9 1.4-.9 1.3 0 2.9.4 4.2 1.1V7.2C8.8 6.6 7.4 6.4 6 6.4c-3.4 0-5.7 1.8-5.7 4.7 0 4.6 6.4 3.9 6.4 5.9 0 .8-.7 1-1.6 1-1.4 0-3.2-.6-4.6-1.4v4.1c1.5.7 3.1 1 4.6 1 3.5 0 5.9-1.7 5.9-4.7 0-5-6.4-4.2-6.4-5.8z"/></svg>
      </div>
    );
  }
  if (brand === "apple") return <div className={cls}><svg viewBox="0 0 24 24" className="w-5 h-5 fill-white"><path d="M17.05 12.91c-.03-2.52 2.05-3.74 2.14-3.8-1.17-1.71-2.99-1.95-3.64-1.98-1.55-.16-3.03.91-3.81.91-.79 0-1.99-.89-3.28-.86-1.69.03-3.25.98-4.11 2.49-1.76 3.05-.45 7.55 1.27 10.02.83 1.21 1.83 2.57 3.13 2.52 1.26-.05 1.74-.81 3.27-.81 1.52 0 1.96.81 3.3.78 1.36-.02 2.23-1.23 3.06-2.45.97-1.4 1.37-2.78 1.4-2.85-.03-.01-2.69-1.03-2.73-4.07zM14.62 5.18c.69-.84 1.16-2.01 1.03-3.18-1 .04-2.21.67-2.93 1.51-.64.74-1.21 1.93-1.06 3.08 1.12.09 2.26-.57 2.96-1.41z"/></svg></div>;
  if (brand === "google") return <div className={cls}><svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09a6.61 6.61 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg></div>;
  if (brand === "klarna") return <div className={`${cls} bg-[#FFB3C7] border-[#FFB3C7] text-black font-extrabold text-[11px]`}>Klarna</div>;
  if (brand === "santander") return <div className={`${cls} bg-[#EC0000] border-[#EC0000] text-white font-extrabold text-[9px] tracking-tight px-1`}>Santander</div>;
  return null;
}

function ReviewStep({ contact, shippingObj, shippingFee, payment, santanderPlan, monthlyKlarna, monthlySantander, acceptedTerms, setAcceptedTerms, onEdit }) {
  const meta = PAYMENT_METHODS.find((m) => m.id === payment);

  return (
    <Card title="Bestellung überprüfen" subtitle="Schritt 4 von 4" data-testid="step-review">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ReviewBlock title="Lieferadresse" onEdit={() => onEdit(1)} testid="review-address">
          <div className="text-sm text-zinc-300">{contact.first_name} {contact.last_name}</div>
          <div className="text-sm text-zinc-400">{contact.street}</div>
          <div className="text-sm text-zinc-400">{contact.zip} {contact.city}</div>
          <div className="text-sm text-zinc-400">{contact.country}</div>
          <div className="text-xs text-zinc-500 mt-2">{contact.email} · {contact.phone}</div>
        </ReviewBlock>
        <ReviewBlock title="Versand" onEdit={() => onEdit(2)} testid="review-shipping">
          <div className="text-sm text-zinc-300">{shippingObj.name}</div>
          <div className="text-xs text-zinc-500 mt-1">{shippingObj.desc}</div>
          <div className="text-sm font-display font-semibold text-white numeric mt-2">{shippingFee === 0 ? "Gratis" : fmt(shippingFee)}</div>
        </ReviewBlock>
        <ReviewBlock title="Zahlung" onEdit={() => onEdit(3)} testid="review-payment" full>
          <div className="flex items-center gap-3">
            <BrandLogo brand={meta?.brand} />
            <div className="flex-1">
              <div className="text-sm text-zinc-300">{meta?.name}</div>
              {payment === "card" && <div className="text-xs text-zinc-500 mt-0.5">Kartendaten werden sicher bei Stripe eingegeben</div>}
              {payment === "klarna" && <div className="text-xs text-zinc-500 mt-0.5">3 Raten à {fmt(monthlyKlarna)}</div>}
              {payment === "santander" && <div className="text-xs text-zinc-500 mt-0.5">{santanderPlan} Monate à {fmt(monthlySantander)}</div>}
            </div>
          </div>
        </ReviewBlock>
      </div>

      <label className="mt-5 flex items-start gap-2 cursor-pointer" data-testid="terms-label">
        <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-0.5 accent-[#DC1928]" data-testid="accept-terms" />
        <span className="text-xs text-zinc-400">
          Ich habe die <span className="text-white underline">AGB</span> und die <span className="text-white underline">Datenschutzerklärung</span> gelesen und akzeptiere diese.
          Ich stimme zu, dass meine Daten zur Auftragsabwicklung verarbeitet werden.
        </span>
      </label>
    </Card>
  );
}

function ReviewBlock({ title, children, onEdit, full, testid }) {
  return (
    <div className={`p-4 rounded-2xl border border-white/10 bg-[#0F0F11] ${full ? "md:col-span-2" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-zinc-500">{title}</div>
        <button onClick={onEdit} className="text-[11px] text-[#FF2D40] hover:underline" data-testid={`${testid}-edit`}>Ändern</button>
      </div>
      <div>{children}</div>
    </div>
  );
}

function OrderSummary({ items, subtotal, shippingObj, shippingFee, tax, grandTotal, payment, monthlyKlarna, monthlySantander, santanderPlan, discount, discountAmount, discountInput, setDiscountInput, discountStatus, applyDiscount, removeDiscount }) {
  return (
    <div className="lg:sticky lg:top-28 rounded-3xl bg-[#141417] border border-white/5 p-6 md:p-7" data-testid="order-summary">
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-lg font-semibold">Bestellübersicht</h3>
        <span className="text-xs text-zinc-500">{items.length} {items.length === 1 ? "Artikel" : "Artikel"}</span>
      </div>

      <div className="mt-5 space-y-3 max-h-[280px] overflow-y-auto pr-1">
        {items.map((it) => (
          <div key={it.sku} className="flex gap-3" data-testid={`summary-${it.sku}`}>
            <div className="relative w-14 h-14 rounded-xl bg-[#0A0A0B] overflow-hidden shrink-0 border border-white/5">
              <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC1928] text-white text-[10px] font-semibold flex items-center justify-center">{it.qty}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white line-clamp-1">{it.name}</div>
              {it.compatible && <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{it.compatible}</div>}
            </div>
            <div className="text-sm text-white font-display font-semibold numeric whitespace-nowrap">{fmt(it.price * it.qty)}</div>
          </div>
        ))}
      </div>

      {/* DISCOUNT INPUT */}
      <DiscountField
        discount={discount}
        discountInput={discountInput}
        setDiscountInput={setDiscountInput}
        discountStatus={discountStatus}
        applyDiscount={applyDiscount}
        removeDiscount={removeDiscount}
      />

      <div className="mt-6 pt-5 border-t border-white/5 space-y-2 text-sm">
        <Row label="Zwischensumme (inkl. USt)" value={fmt(subtotal)} />
        <Row label="Versand" value={shippingFee === 0 ? <span className="text-emerald-400">Gratis</span> : fmt(shippingFee)} />
        {discount && (
          <Row
            label={
              <span className="inline-flex items-center gap-1.5 text-emerald-400">
                <Tag className="w-3.5 h-3.5" /> Rabatt <span className="font-mono text-xs uppercase tracking-wider">{discount.code}</span> · -{discount.percent}%
              </span>
            }
            value={<span className="text-emerald-400">−{fmt(discountAmount)}</span>}
            testid="summary-discount-row"
          />
        )}
        <Row label="davon USt. (20%)" value={fmt(tax)} muted />
      </div>

      <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
        <span className="font-display text-base">Gesamt <span className="text-zinc-500 text-xs font-normal">inkl. USt</span></span>
        <span className="font-display text-3xl font-bold numeric tracking-tight" data-testid="summary-grand-total">{fmt(grandTotal)}</span>
      </div>

      {payment === "klarna" && (
        <div className="mt-4 p-3 rounded-xl bg-[#FFB3C7]/10 border border-[#FFB3C7]/20 text-xs">
          <div className="text-[#FFB3C7] font-semibold mb-1">Klarna · 3 Raten</div>
          <div className="text-zinc-400">3× {fmt(monthlyKlarna)} · 0% Zinsen</div>
        </div>
      )}
      {payment === "santander" && (
        <div className="mt-4 p-3 rounded-xl bg-[#EC0000]/10 border border-[#EC0000]/20 text-xs">
          <div className="text-[#FF5555] font-semibold mb-1">Santander Ratenkauf</div>
          <div className="text-zinc-400">{santanderPlan} Monate · {fmt(monthlySantander)} / Monat</div>
        </div>
      )}

      <div className="mt-5 pt-5 border-t border-white/5 space-y-2.5 text-[11px] text-zinc-500">
        <Bullet>6 Monate Garantie auf alle Produkte</Bullet>
        <Bullet>14 Tage Rückgaberecht</Bullet>
        <Bullet>Versand aus Wien · Klimaneutral</Bullet>
      </div>
    </div>
  );
}

function DiscountField({ discount, discountInput, setDiscountInput, discountStatus, applyDiscount, removeDiscount }) {
  if (discount) {
    return (
      <div
        className="mt-5 p-3 rounded-2xl bg-emerald-500/8 border border-emerald-500/25 flex items-center gap-2.5"
        data-testid="discount-applied"
      >
        <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
          <Tag className="w-4 h-4 text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-emerald-200 font-bold">Code aktiv</div>
          <div className="font-mono text-sm text-white truncate" data-testid="discount-applied-code">
            {discount.code} <span className="text-emerald-300">· -{discount.percent}%</span>
          </div>
        </div>
        <button
          type="button"
          onClick={removeDiscount}
          className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-white/8 hover:bg-white/15 text-zinc-300"
          title="Code entfernen"
          data-testid="discount-remove-btn"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-5" data-testid="discount-input-wrap">
      <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold inline-flex items-center gap-1.5">
        <Tag className="w-3.5 h-3.5" /> Rabattcode
      </Label>
      <div className="mt-1.5 flex gap-2">
        <Input
          value={discountInput}
          onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyDiscount(); } }}
          placeholder="Rabattcode eingeben"
          className="bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#DC1928] focus:ring-0 h-11 font-mono uppercase tracking-wider"
          maxLength={32}
          data-testid="discount-input"
        />
        <Button
          type="button"
          onClick={applyDiscount}
          disabled={discountStatus.loading || !discountInput.trim()}
          className="bg-white/10 hover:bg-white/20 text-white rounded-full h-11 px-5 shrink-0 disabled:opacity-50"
          data-testid="discount-apply-btn"
        >
          {discountStatus.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Einlösen"}
        </Button>
      </div>
      {discountStatus.error && (
        <p className="mt-1.5 text-xs text-red-400" data-testid="discount-error">{discountStatus.error}</p>
      )}
    </div>
  );
}

function Row({ label, value, muted, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="text-zinc-400">{label}</span>
      <span className={`font-display font-medium numeric ${muted ? "text-zinc-400" : "text-white"}`}>{value}</span>
    </div>
  );
}
function Bullet({ children }) {
  return (
    <div className="flex items-center gap-2">
      <Check className="w-3 h-3 text-emerald-400" />
      <span>{children}</span>
    </div>
  );
}
